// src/components/JobView/Tabs/GangRunTab.js
// Pestaña de un job tipo 'GangRun': ver/editar las líneas (PLAN-GANG-RUN Fase A).
// Cada línea = Cliente + SKU del catálogo + cantidad pedida + ups (modo PTO).
import React, { useState, useEffect, useCallback } from "react";
import { collection, getDocs, getDoc, doc, updateDoc } from "firebase/firestore";
import GangLinesEditor from "../../GangRun/GangLinesEditor";
import { setGangLines, suggestSheets } from "../../../lib/gangrun";
import { resolveCategories } from "../../../lib/productCategories";

const GangRunTab = ({ jobId, jobData, db, user, storage }) => {
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [lines, setLines] = useState(jobData?.gangLines || []);
  const [hdr, setHdr] = useState({ materialId: "", width: "", height: "", unit: "in" });

  useEffect(() => { setLines(jobData?.gangLines || []); }, [jobData]);
  useEffect(() => {
    setHdr({
      materialId: jobData?.materialId || "",
      width: jobData?.sheet?.width || "",
      height: jobData?.sheet?.height || "",
      unit: jobData?.sheet?.unit || "in",
    });
  }, [jobData]);

  const saveHeader = async (patch) => {
    const next = { ...hdr, ...patch };
    setHdr(next);
    const mat = materials.find((m) => m.id === next.materialId);
    await updateDoc(doc(db, "jobs", jobId), {
      materialId: next.materialId || null,
      materialName: mat?.name || "",
      sheet: { width: Number(next.width) || null, height: Number(next.height) || null, unit: next.unit },
    });
  };

  const load = useCallback(async () => {
    const [cSnap, pSnap, mSnap, cfg] = await Promise.all([
      getDocs(collection(db, "clients")),
      getDocs(collection(db, "products")),
      getDocs(collection(db, "materials")),
      getDoc(doc(db, "project_settings", "productCategories")),
    ]);
    setClients(cSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    setProducts(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setMaterials(mSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    setCategories(resolveCategories(cfg.exists() ? cfg.data().categories : null));
  }, [db]);
  useEffect(() => { load(); }, [load]);

  const onChange = async (newLines) => {
    setLines(newLines);
    await setGangLines(db, jobId, newLines);
  };

  return (
    <div className="space-y-3">
      <h2 className="text-xl font-bold text-gray-800">Gang Run / Combo</h2>

      {/* Material + Hoja editables (se guardan al cambiar) */}
      <div className="flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Material</label>
          <select value={hdr.materialId} onChange={(e) => saveHeader({ materialId: e.target.value })} className="p-2 border border-gray-300 rounded-md text-sm min-w-[180px]">
            <option value="">— Material —</option>
            {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">Hoja (An×Al)</label>
          <div className="flex gap-1 items-center">
            <input type="number" placeholder="An" value={hdr.width} onChange={(e) => setHdr({ ...hdr, width: e.target.value })} onBlur={(e) => saveHeader({ width: e.target.value })} className="w-20 p-2 border border-gray-300 rounded-md text-sm" />
            <span className="text-gray-400">×</span>
            <input type="number" placeholder="Al" value={hdr.height} onChange={(e) => setHdr({ ...hdr, height: e.target.value })} onBlur={(e) => saveHeader({ height: e.target.value })} className="w-20 p-2 border border-gray-300 rounded-md text-sm" />
            <select value={hdr.unit} onChange={(e) => saveHeader({ unit: e.target.value })} className="p-2 border border-gray-300 rounded-md text-sm">
              <option value="in">in</option><option value="mm">mm</option><option value="ft">ft</option>
            </select>
          </div>
        </div>
        <div className="ml-auto text-sm text-gray-500">Hojas a correr: <b>{suggestSheets(lines)}</b></div>
      </div>
      <p className="text-xs text-gray-400">Producir-a-orden (PTO): cada línea cumple la orden de su cliente. (Producir-a-stock / PTI: cableado, próxima fase.)</p>
      <GangLinesEditor db={db} user={user} storage={storage} gangMaterialId={jobData?.materialId} clients={clients} products={products} categories={categories} materials={materials} sheet={jobData?.sheet} lines={lines} onChange={onChange} reload={load} />
    </div>
  );
};

export default GangRunTab;
